type Locale = string;

class Translator<T extends object = object> {
  private selectedLocale: Locale = '';
  private updateListeners: Array<(locale: Locale) => void> = [];
  private translations: { [key: string]: T } = {};

  constructor(translations: { [key: string]: T }) {
    this.translations = translations;

    // Set initial HTML lang attribute on page load
    document.documentElement.lang = this.locale;
  }

  /**
   * Register to get notified once the locale changes. This
   * can be used to e.g. rerender a react component.
   */
  public onUpdate(callback: (locale: string) => void): void {
    this.updateListeners.push(callback);
  }

  /**
   * Gets the available locales. Currently this just returns
   * all keys of this._translations, e.g. ['de', 'en']
   */
  get availableLocales(): Locale[] {
    return Object.keys(this.translations);
  }

  /**
   * Access the currently set locale. This might be a bit ahead
   * of time in case the locale data is not loaded yet.
   */
  get locale(): Locale {
    return (
      this.selectedLocale ||
      localStorage.getItem('locale') ||
      ['en', 'de'].filter((e) => !!e && navigator.language.startsWith(e))[0] ||
      'en'
    );
  }

  /**
   * Set a new locale. This will notify all observers of the changed
   * locale as soon as the locale data has been downloaded.
   */
  set locale(locale: Locale) {
    this.setLocale(locale);
    localStorage.setItem('locale', locale);
  }

  private async setLocale(locale: Locale): Promise<void> {
    this.selectedLocale = locale;
    this.updateListeners.forEach((e) => e(locale));
  }

  public get translate(): T {
    const handler = {
      get: (target: Translator<T>, p: string | symbol) => target.translations[target.locale][p],
    };
    return new Proxy(this, handler) as unknown as T;
  }
}

import en from './en';
import de from './de';
export const I18n = new Translator<typeof en>({ en, de });
export const t = I18n.translate;
